import type { InviteUserInput, UserInfo } from "../../types";
import { Schemas } from "../../schemas";
import { sigv4ApiClient } from "./internal/sigv4ApiClient";

const API_PATH = "/v1/user";

const normalizeId = (value: string): string => {
  return value.trim();
};

const normalizeUser = (payload: unknown): UserInfo => {
  if (typeof payload === "object" && payload !== null && !Array.isArray(payload)) {
    const record = payload as Record<string, unknown>;
    if (typeof record.id === "string" && record.id.trim().length > 0) {
      return Schemas.user.parse(record);
    }
    if (typeof record.email === "string" && record.email.trim().length > 0) {
      return Schemas.user.parse({
        ...record,
        id: record.email,
      });
    }
  }
  return Schemas.user.parse(payload);
};

export const userService = {
  list: async (): Promise<UserInfo[]> => {
    const users = await sigv4ApiClient.list<unknown>(API_PATH);
    return users.map(normalizeUser);
  },
  invite: async (user: InviteUserInput): Promise<UserInfo> => {
    const invited = await sigv4ApiClient.post<unknown>(API_PATH, user);
    return normalizeUser(invited);
  },
  get: async (id: string): Promise<UserInfo | undefined> => {
    const normalized = normalizeId(id);
    return (await userService.list()).find((user) => user.id === normalized);
  },
  patch: async (
    id: string,
    patch: Partial<Omit<UserInfo, "email">>,
  ): Promise<UserInfo> => {
    const normalized = normalizeId(id);
    const current = await userService.get(normalized);
    if (!current) {
      throw new Error(`User with id "${normalized}" not found`);
    }

    const payload = await sigv4ApiClient.put<unknown>(API_PATH, {
      ...current,
      ...patch,
      id: normalized,
    });
    return normalizeUser(payload);
  },
  delete: async (id: string): Promise<boolean> => {
    const normalized = normalizeId(id);
    await sigv4ApiClient.delete(`${API_PATH}/${encodeURIComponent(normalized)}`);
    return true;
  },
};
